// Unicode superscript ("daraja") helpers for MfMarkdownEditor.
//
// The editor stores markdown and the public site renders it with a plain
// markdown pipeline, so pandoc's `^1^` syntax and raw <sup> tags would both
// leak through as literal text there (Markdown is configured with html:false).
// Unicode superscript characters need no renderer support at all: 167¹ stays
// 167¹ in markdown, in the API, in Excel exports and in plain-text search.
//
// Only digits and math signs are mapped. Superscript letters do exist in
// Unicode but incompletely (no `q`, no uppercase C/F/Q/S/X/Y/Z) and they render
// unevenly across fonts, so a mixed selection would convert only half of its
// characters — better to leave them out than to produce ragged text.

const TO_SUPERSCRIPT = {
  "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴",
  "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹",
  "+": "⁺", "-": "⁻", "−": "⁻", "=": "⁼", "(": "⁽", ")": "⁾",
};

const FROM_SUPERSCRIPT = Object.entries(TO_SUPERSCRIPT).reduce((acc, [plain, sup]) => {
  // "-" and "−" share ⁻; the first (ASCII hyphen) wins when converting back.
  if (!acc[sup]) acc[sup] = plain;
  return acc;
}, {});

// Unmapped characters (letters, spaces, punctuation) pass through unchanged.
export function toSuperscript(text) {
  return String(text ?? "").replace(/./gu, (char) => TO_SUPERSCRIPT[char] || char);
}

export function fromSuperscript(text) {
  return String(text ?? "").replace(/./gu, (char) => FROM_SUPERSCRIPT[char] || char);
}

// True when the text is entirely superscript already — the toolbar uses this to
// light the button up and to turn a second click into "revert to normal".
export function isSuperscript(text) {
  const value = String(text ?? "");
  if (!value) return false;
  return [...value].every((char) => !!FROM_SUPERSCRIPT[char]);
}

// True when at least one character can actually be raised.
export function hasSuperscriptForm(text) {
  return [...String(text ?? "")].some((char) => !!TO_SUPERSCRIPT[char]);
}
